// Cross-Module Integration Coordinator - Thin facade delegating to focused sub-modules
// ✅ NO MAGIC STRINGS: Use typed enums and constants
//
// Responsibilities are split across three helper modules:
//   - cross-module-event-router.ts : event emission, queuing, routing, retry
//   - cross-module-state-sync.ts   : module state + dependency tracking, state sync
//   - cross-module-health.ts       : health checks + performance metrics
//
// This class wires them together and exposes the unified public API.

import { EventBus } from "../events/EventBus";
import debugLogger from "../utils/debugLogger";

import { CrossModuleEventRouter } from "./cross-module-event-router";
import { CrossModuleHealth } from "./cross-module-health";
import { CrossModuleStateSync } from "./cross-module-state-sync";
import {
  CrossModuleEvent,
  ModuleInteraction,
  ModuleDependency,
  IntegrationConfig,
  IntegrationMetrics,
  CrossModulePayload,
  ModuleState,
  EventRoutingPattern,
  ModuleHealthStatus,
  PerformanceMetrics,
} from "./types";

export class CrossModuleIntegration {
  private eventBus: EventBus;
  private config: IntegrationConfig;
  private metrics: IntegrationMetrics;
  private moduleInteractions: Map<string, ModuleInteraction[]> = new Map();
  private moduleDependencies: Map<string, ModuleDependency[]> = new Map();
  private moduleStates: Map<string, ModuleState> = new Map();
  private eventQueue: Array<{
    event: string;
    payload: CrossModulePayload;
    timestamp: number;
    priority: number;
  }> = [];
  private isProcessingQueue = false;
  private eventRoutingPatterns: Map<string, EventRoutingPattern> = new Map();
  private performanceMetrics: Map<string, PerformanceMetrics> = new Map();
  private healthChecks: Map<string, ModuleHealthStatus> = new Map();
  private eventRetryMap: Map<string, { retries: number; lastAttempt: number }> =
    new Map();

  private eventRouter: CrossModuleEventRouter;
  private stateSync: CrossModuleStateSync;
  private health: CrossModuleHealth;

  constructor(eventBus: EventBus, config: Partial<IntegrationConfig> = {}) {
    this.eventBus = eventBus;
    this.config = {
      enableCrossModuleEvents: true,
      enableDependencyTracking: true,
      enableMetrics: true,
      enableStateSynchronization: true,
      enableAdvancedRouting: true,
      enablePerformanceMonitoring: true,
      enableHealthChecks: true,
      enableEventRetry: true,
      maxEventRetries: 3,
      eventTimeout: 5000,
      maxQueueSize: 1000,
      batchProcessingSize: 10,
      healthCheckInterval: 30000,
      performanceThreshold: 100,
      ...config,
    };

    this.metrics = {
      totalCrossModuleEvents: 0,
      successfulEvents: 0,
      failedEvents: 0,
      moduleInteractions: 0,
      dependencyResolutions: 0,
      averageEventLatency: 0,
      eventQueueSize: 0,
      stateSyncRequests: 0,
      stateSyncResponses: 0,
      routingPatternMatches: 0,
      retryAttempts: 0,
      healthCheckFailures: 0,
      performanceViolations: 0,
    };

    this.eventRouter = new CrossModuleEventRouter({
      eventBus: this.eventBus,
      config: this.config,
      metrics: this.metrics,
      moduleInteractions: this.moduleInteractions,
      eventRoutingPatterns: this.eventRoutingPatterns,
      eventQueue: this.eventQueue,
      isProcessingQueue: this.isProcessingQueue,
      eventRetryMap: this.eventRetryMap,
      onModuleRegistered: (moduleName) =>
        this.stateSync.handleModuleRegistered(moduleName),
      onModuleUnregistered: (moduleName) => {
        this.stateSync.handleModuleUnregistered(moduleName);
        this.health.handleModuleUnregistered(moduleName);
      },
      onInteractionComplete: (moduleName, latency, success) =>
        this.health.updatePerformanceMetrics(moduleName, latency, success),
    });

    this.stateSync = new CrossModuleStateSync({
      eventBus: this.eventBus,
      config: this.config,
      metrics: this.metrics,
      moduleDependencies: this.moduleDependencies,
      moduleStates: this.moduleStates,
    });

    this.health = new CrossModuleHealth({
      config: this.config,
      metrics: this.metrics,
      moduleStates: this.moduleStates,
      performanceMetrics: this.performanceMetrics,
      healthChecks: this.healthChecks,
    });

    this.setupEventHandlers();
    this.setupAdvancedRouting();
    this.setupPerformanceMonitoring();
    this.setupHealthChecks();
    debugLogger.info(
      "integration",
      "Cross-Module Integration initialized with advanced features",
    );
  }

  /**
   * Register a cross-module event handler with advanced routing
   */
  public registerCrossModuleHandler(
    sourceModule: string,
    targetModule: string,
    eventType: CrossModuleEvent,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    handler: (payload: any) => Promise<void>,
    routingPattern?: EventRoutingPattern,
  ): string {
    return this.eventRouter.registerCrossModuleHandler(
      sourceModule,
      targetModule,
      eventType,
      handler,
      routingPattern,
    );
  }

  /**
   * Emit a cross-module event with advanced routing and queuing
   */
  public async emitCrossModuleEvent(
    sourceModule: string,
    targetModule: string,
    eventType: CrossModuleEvent,
    payload: CrossModulePayload,
    priority: number = 1,
  ): Promise<void> {
    await this.eventRouter.emitCrossModuleEvent(
      sourceModule,
      targetModule,
      eventType,
      payload,
      priority,
    );
  }

  /**
   * Register module dependencies with state tracking
   */
  public registerModuleDependency(
    moduleName: string,
    dependency: ModuleDependency,
  ): void {
    this.stateSync.registerModuleDependency(moduleName, dependency);
  }

  /**
   * Synchronize module states across the system
   */
  public async synchronizeModuleStates(): Promise<void> {
    await this.stateSync.synchronizeModuleStates();
  }

  /**
   * Get module state information
   */
  public getModuleState(moduleName: string): ModuleState | null {
    return this.stateSync.getModuleState(moduleName);
  }

  /**
   * Get all module states
   */
  public getAllModuleStates(): ModuleState[] {
    return this.stateSync.getAllModuleStates();
  }

  /**
   * Update module state
   */
  public updateModuleState(
    moduleName: string,
    updates: Partial<ModuleState>,
  ): void {
    this.stateSync.updateModuleState(moduleName, updates);
  }

  /**
   * Get integration configuration
   */
  public getConfig(): IntegrationConfig {
    return { ...this.config };
  }

  /**
   * Update integration configuration
   */
  public updateConfig(updates: Partial<IntegrationConfig>): void {
    this.config = { ...this.config, ...updates };
    debugLogger.info(
      "integration",
      "Integration configuration updated",
      updates,
    );
  }

  /**
   * Get integration metrics
   */
  public getMetrics(): IntegrationMetrics {
    return { ...this.metrics };
  }

  /**
   * Clear all cross-module handlers
   */
  public clearHandlers(): void {
    this.eventRouter.clearHandlers();
  }

  /**
   * Reset integration metrics
   */
  public resetMetrics(): void {
    this.metrics = {
      totalCrossModuleEvents: 0,
      successfulEvents: 0,
      failedEvents: 0,
      moduleInteractions: 0,
      dependencyResolutions: 0,
      averageEventLatency: 0,
      eventQueueSize: 0,
      stateSyncRequests: 0,
      stateSyncResponses: 0,
      routingPatternMatches: 0,
      retryAttempts: 0,
      healthCheckFailures: 0,
      performanceViolations: 0,
    };
    debugLogger.info("integration", "Integration metrics reset");
  }

  /**
   * Get module dependencies
   */
  public getModuleDependencies(moduleName: string): ModuleDependency[] {
    return this.stateSync.getModuleDependencies(moduleName);
  }

  /**
   * Get module interactions
   */
  public getModuleInteractions(moduleName: string): ModuleInteraction[] {
    const interactions: ModuleInteraction[] = [];
    this.moduleInteractions.forEach((moduleInteractions) => {
      moduleInteractions.forEach((interaction) => {
        if (
          interaction.sourceModule === moduleName ||
          interaction.targetModule === moduleName
        ) {
          interactions.push(interaction);
        }
      });
    });
    return interactions;
  }

  /**
   * Get module health status
   */
  public getModuleHealthStatus(moduleName: string): ModuleHealthStatus {
    return this.health.getModuleHealthStatus(moduleName);
  }

  /**
   * Get performance metrics for a module
   */
  public getModulePerformanceMetrics(
    moduleName: string,
  ): PerformanceMetrics | null {
    return this.health.getModulePerformanceMetrics(moduleName);
  }

  private setupEventHandlers(): void {
    this.eventRouter.setupEventHandlers();
  }

  private setupAdvancedRouting(): void {
    this.eventRouter.setupAdvancedRouting();
  }

  private setupPerformanceMonitoring(): void {
    this.health.setupPerformanceMonitoring();
  }

  private setupHealthChecks(): void {
    this.health.setupHealthChecks();
  }
}
